import { CustomPanel, DropdownPrimeReact } from "@finanedi.es/storybook"
import {
  faAngleDown,
  faAngleRight,
  faTimes,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { SITUACION_INMUEBLE_MODELO_347_ENUM } from "api/Types/SituacionInmueble"
import { TIPO_NUMERACION } from "api/Types/TipoNumeracion"
import { TFunction } from "i18next"
import { InputText } from "primereact/inputtext"
import { useEffect, useState } from "react"
import { FieldErrors, UseFormSetValue } from "react-hook-form"
import { getProvinceIneOptions } from "utils/provinceDropdownOptions"
import {
  FieldTemplateGeneric,
  renderFieldTemplateGeneric,
} from "../../../PlantillasBloquesTabla"
import { Modelo347Form } from "../DeclaracionModelo347"

const InmueblesTemplate = ({
  t,
  text,
  values,
  setValue,
  inmuebleIndex,
  handleDelete,
  errors,
}: {
  t: TFunction
  text: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>
  inmuebleIndex: number
  handleDelete: () => void
  errors: FieldErrors<Modelo347Form>
}) => {
  const [categoryExpanded, setCategoryExpanded] = useState(true)
  const ANCHOR_REF =
    "https://www1.sedecatastro.gob.es/cycbieninmueble/ovcbusqueda.aspx"
  const fieldsInmuebles: FieldTemplateGeneric[] = [
    {
      id: "row1",
      inputsOne: [
        {
          label: t("declaracionModelo347.vistaInmuebles.nifArrendatario"),
          code: `inmuebles.${inmuebleIndex}.nifArrendatario`,
          value: values?.inmuebles?.[inmuebleIndex]?.nifArrendatario,
          inputType: "text",
          colSpan: "tw-col-span-2",
        },
        {
          label: t("declaracionModelo347.vistaInmuebles.nifRepresentanteLegal"),
          code: `inmuebles.${inmuebleIndex}.nifRepresentanteLegal`,
          value: values?.inmuebles?.[inmuebleIndex]?.nifRepresentanteLegal,
          inputType: "text",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      id: "row2",
      inputsOne: [
        {
          label: t(
            "declaracionModelo347.vistaInmuebles.apellidosNombreRazonSocialDenominacion"
          ),
          code: `inmuebles.${inmuebleIndex}.denominacionArrendatario`,
          value: values?.inmuebles?.[inmuebleIndex]?.denominacionArrendatario,
          inputType: "text",
          colSpan: "tw-col-span-2",
          mandatory: true,
          error:
            errors?.inmuebles?.[inmuebleIndex]?.denominacionArrendatario
              ?.message,
        },
        {
          label: t("declaracionModelo347.vistaInmuebles.importeOperacion"),
          code: `inmuebles.${inmuebleIndex}.importeOperacion`,
          value: values?.inmuebles?.[inmuebleIndex]?.importeOperacion,
          inputType: "currency",
          colSpan: "tw-col-span-2",
          mandatory: true,
          error: errors?.inmuebles?.[inmuebleIndex]?.importeOperacion?.message,
        },
      ],
    },
  ]
  const fieldsInmueblesTwo: FieldTemplateGeneric = {
    id: "row3",
    inputsOne: [
      {
        label: t("taxSummary.declaracion.addressForm.situacionInmueble"),
        code: `inmuebles.${inmuebleIndex}.situacionInmueble`,
        value: values?.inmuebles?.[inmuebleIndex]?.situacionInmueble,
        inputType: "dropdown",
        options: [
          {
            label: t(
              "declaracionModelo347.vistaInmuebles.dropdownOptions.optionOne"
            ),
            value:
              SITUACION_INMUEBLE_MODELO_347_ENUM.EXCEPTO_PAIS_VASCO_Y_NAVARRA,
          },
          {
            label: t(
              "declaracionModelo347.vistaInmuebles.dropdownOptions.optionTwo"
            ),
            value: SITUACION_INMUEBLE_MODELO_347_ENUM.PAIS_VASCO_O_NAVARRA,
          },
          {
            label: t(
              "declaracionModelo347.vistaInmuebles.dropdownOptions.optionThree"
            ),
            value:
              SITUACION_INMUEBLE_MODELO_347_ENUM.SITUACIONES_ANTERIORES_SIN_REFEERENCIA_CATASTRAL,
          },
          {
            label: t(
              "declaracionModelo347.vistaInmuebles.dropdownOptions.optionFour"
            ),
            value: SITUACION_INMUEBLE_MODELO_347_ENUM.EXTRANJERO,
          },
        ],
        colSpan: "tw-col-span-2",
        mandatory: true,
        error: errors?.inmuebles?.[inmuebleIndex]?.situacionInmueble?.message,
      },
      {
        label: t("taxSummary.declaracion.addressForm.referenciaCatastral"),
        code: `inmuebles.${inmuebleIndex}.referenciaCatastral`,
        value: values?.inmuebles?.[inmuebleIndex]?.referenciaCatastral ?? "",
        inputType: "text",
        colSpan: "tw-col-span-2",
      },
    ],
  }

  const fieldsInmueblesThree: FieldTemplateGeneric[] = [
    {
      id: "row4",
      inputsOne: [
        {
          label: t("taxSummary.declaracion.addressForm.tipoVia"),
          code: `inmuebles.${inmuebleIndex}.tipoVia`,
          value: values?.inmuebles?.[inmuebleIndex]?.tipoVia,
          inputType: "dropdown",
          colSpan: "tw-col-span-2",
          options: [
            {
              label: t("taxSummary.declaracion.addressForm.calle"),
              value: "1",
            },
            {
              label: t("taxSummary.declaracion.addressForm.puerta"),
              value: "2",
            },
            {
              label: t("taxSummary.declaracion.addressForm.avenida"),
              value: "3",
            },
            {
              label: t("taxSummary.declaracion.addressForm.plaza"),
              value: "4",
            },
          ],
        },
        {
          label: t("taxSummary.declaracion.addressForm.nombreVia"),
          code: `inmuebles.${inmuebleIndex}.nombreViaPublica`,
          value: values?.inmuebles?.[inmuebleIndex]?.nombreViaPublica,
          inputType: "text",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      id: "row5",
      inputsOne: [
        {
          label: t("taxSummary.declaracion.addressForm.tipoNumeracion"),
          code: `inmuebles.${inmuebleIndex}.tipoNumeracion`,
          value: values?.inmuebles?.[inmuebleIndex]?.tipoNumeracion,
          inputType: "dropdown",
          options: [
            {
              label: t("taxSummary.declaracion.addressForm.numero"),
              value: TIPO_NUMERACION.NUMERO,
            },
            {
              label: t("taxSummary.declaracion.addressForm.sinNumero"),
              value: TIPO_NUMERACION.SIN_NUMERO,
            },
            {
              label: t("taxSummary.declaracion.addressForm.kilometro"),
              value: TIPO_NUMERACION.KILOMETRO,
            },
            {
              label: t("taxSummary.declaracion.addressForm.otros"),
              value: TIPO_NUMERACION.OTROS,
            },
          ],
        },
        {
          label: t("taxSummary.declaracion.addressForm.numeroCasa"),
          code: `inmuebles.${inmuebleIndex}.numeroCasa`,
          value: values?.inmuebles?.[inmuebleIndex]?.numeroCasa,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.calificadorNumero"),
          code: `inmuebles.${inmuebleIndex}.calificadorNumero`,
          value: values?.inmuebles?.[inmuebleIndex]?.calificadorNumero,
          disabled: true,
          inputType: "text",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      id: "row6",
      inputsOne: [
        {
          label: t("taxSummary.declaracion.addressForm.bloque"),
          code: `inmuebles.${inmuebleIndex}.bloque`,
          value: values?.inmuebles?.[inmuebleIndex]?.bloque,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.portal"),
          code: `inmuebles.${inmuebleIndex}.portal`,
          value: values?.inmuebles?.[inmuebleIndex]?.portal,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.escalera"),
          code: `inmuebles.${inmuebleIndex}.escalera`,
          value: values?.inmuebles?.[inmuebleIndex]?.escalera,
          inputType: "text",
        },
      ],
    },
    {
      id: "row7",
      inputsOne: [
        {
          label: t("taxSummary.declaracion.addressForm.plantaPiso"),
          code: `inmuebles.${inmuebleIndex}.plantaPiso`,
          value: values?.inmuebles?.[inmuebleIndex]?.plantaPiso,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.puerta"),
          code: `inmuebles.${inmuebleIndex}.puerta`,
          value: values?.inmuebles?.[inmuebleIndex]?.puerta,
          inputType: "text",
        },
        {
          label: t("declaracionModelo347.vistaInmuebles.complemento"),
          code: `inmuebles.${inmuebleIndex}.complemento`,
          value: values?.inmuebles?.[inmuebleIndex]?.complemento,
          inputType: "text",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      id: "row8",
      inputsOne: [
        {
          label: t("declaracionModelo347.vistaInmuebles.localidadPoblacion"),
          code: `inmuebles.${inmuebleIndex}.localidadPoblacion`,
          value: values?.inmuebles?.[inmuebleIndex]?.localidadPoblacion,
          inputType: "text",
          colSpan: "tw-col-span-2",
        },
        {
          label: t("taxSummary.declaracion.addressForm.municipio"),
          code: `inmuebles.${inmuebleIndex}.municipio`,
          value: values?.inmuebles?.[inmuebleIndex]?.municipio,
          inputType: "text",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      id: "row9",
      inputsOne: [
        {
          label: t("taxSummary.declaracion.addressForm.codigoProvincia"),
          code: `inmuebles.${inmuebleIndex}.provincia`,
          value: values?.inmuebles?.[inmuebleIndex]?.provincia,
          inputType: "dropdown",
          options: getProvinceIneOptions(t),
        },
        {
          label: t("taxSummary.declaracion.addressForm.codigoMunicipio"),
          code: `inmuebles.${inmuebleIndex}.codigoMunicipio`,
          value: values?.inmuebles?.[inmuebleIndex]?.codigoMunicipio,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.codigoPostal"),
          code: `inmuebles.${inmuebleIndex}.codigoPostal`,
          value: values?.inmuebles?.[inmuebleIndex]?.codigoPostal,
          inputType: "number",
        },
      ],
    },
  ]

  const hasFormInmueblesErrors = errors?.inmuebles?.[inmuebleIndex]

  useEffect(() => {
    if (hasFormInmueblesErrors) {
      setCategoryExpanded(false)
    }
  }, [hasFormInmueblesErrors])

  return (
    <CustomPanel
      title={text}
      expandIcon={null}
      collapseIcon={null}
      className='[&>div:first-child]:tw-cursor-pointer tw-font-medium !tw-shadow-none tw-border [&>div:nth-child(2)]:!tw-max-h-[3000px]'
      collapsed={categoryExpanded}
      onToggle={(e) => setCategoryExpanded(e.value)}
      headerTemplate={() => (
        <div
          className='tw-flex tw-justify-between tw-px-4 tw-py-3'
          onClick={() => setCategoryExpanded((prevState) => !prevState)}
        >
          <p>{text}</p>
          <div>
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation()
                handleDelete()
              }}
              className='hover:tw-bg-gray-100 tw-w-6 tw-rounded tw-transition-colors'
            >
              <FontAwesomeIcon
                icon={faTimes}
                className='tw-text-red-500'
              />
            </button>
            <button
              type='button'
              onClick={() => setCategoryExpanded((prevState) => !prevState)}
              className='tw-w-4 tw-ml-4'
            >
              <FontAwesomeIcon
                icon={categoryExpanded ? faAngleDown : faAngleRight}
              />
            </button>
          </div>
        </div>
      )}
    >
      <div className='tw-p-4'>
        {fieldsInmuebles.map((row) => (
          <div
            key={row.id}
            className='tw-w-full tw-h-fit tw-grid tw-grid-cols-4 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25'
          >
            {renderFieldTemplateGeneric({
              fields: row.inputsOne,
              setValue,
              t,
            })}
          </div>
        ))}
        <div className='tw-w-full tw-h-fit tw-grid tw-grid-cols-5 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25'>
          <div className='tw-col-span-3 tw-items-center'>
            <div className='tw-text-left tw-text-xs tw-font-medium'>
              {fieldsInmueblesTwo.inputsOne[0].label}
            </div>
            <DropdownPrimeReact
              value={fieldsInmueblesTwo.inputsOne[0].value}
              options={fieldsInmueblesTwo.inputsOne[0].options}
              optionLabel='label'
              optionValue='value'
              onChange={(e) =>
                setValue(`${fieldsInmueblesTwo.inputsOne[0].code}`, e.value)
              }
              placeholder='Selecciona'
              className='!tw-w-full !tw-h-10 [&_span]:tw-text-sm'
              panelClassName='tw-rounded-md tw-max-w-100 tw-text-sm'
            />
            <small
              className={`${
                fieldsInmueblesTwo.inputsOne[0].error
                  ? "tw-text-danger"
                  : "tw-text-gray-500"
              }`}
            >
              {t("taxSummary.declaracion.mandatoryField")}
            </small>
          </div>
          <div className='tw-col-span-2 tw-items-center'>
            <div className='tw-text-left tw-text-xs tw-font-medium'>
              {fieldsInmueblesTwo.inputsOne[1].label}
            </div>
            <InputText
              className='tw-w-full tw-text-sm'
              value={String(fieldsInmueblesTwo.inputsOne[1].value)}
              onChange={(e) =>
                setValue(
                  `${fieldsInmueblesTwo.inputsOne[1].code}`,
                  e.target.value
                )
              }
            />
            <a
              href={ANCHOR_REF}
              target='_blank'
              rel='noopener noreferrer'
              className='tw-text-left tw-text-xs tw-font-medium tw-text-info hover:tw-underline'
            >
              {t(
                "taxSummary.declaracion.addressForm.buscarReferenciaCatastral"
              )}
            </a>
          </div>
        </div>
        {fieldsInmueblesThree.map((row) => (
          <div
            key={row.id}
            className='tw-w-full tw-h-fit tw-grid tw-grid-cols-4 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25'
          >
            {renderFieldTemplateGeneric({
              fields: row.inputsOne,
              setValue,
              t,
            })}
          </div>
        ))}
      </div>
    </CustomPanel>
  )
}

export default InmueblesTemplate
